import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser, signOut } from 'aws-amplify/auth';
import AdminCoursesOverview from '../admin/AdminCoursesOverview';
import AdminCourseEditor from '../admin/AdminCourseEditor';
import AdminRegistrations from '../admin/AdminRegistrations';
import AdminContacts from '../admin/AdminContacts';
import AdminPartners from '../admin/AdminPartners';
import AdminPartnerEditor from '../admin/AdminPartnerEditor';
import AdminTutors from '../admin/AdminTutors';
import AdminTutorEditor from '../admin/AdminTutorEditor';
import AdminRegistrationEditor from '../admin/AdminRegistrationEditor';
import AdminKeynotesOverview from '../admin/AdminKeynotesOverview';
import AdminKeynoteEditor from '../admin/AdminKeynoteEditor';
import AdminReferences from '../admin/AdminReferences';
import AdminReferenceEditor from '../admin/AdminReferenceEditor';
import AdminInvoices from '../admin/AdminInvoices';
import AdminInvoiceEditor from '../admin/AdminInvoiceEditor';
import { trackAdminAction } from '../utils/matomoEvents';
import './AdminDashboard.css';

interface AdminUser {
  userId: string;
  username: string;
  name?: string;
  loginTime: string;
}

const AdminDashboard: React.FC = () => {
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [activeTab, setActiveTab] = useState<'courses' | 'editor' | 'registrations' | 'contacts' | 'partners' | 'partner-editor' | 'tutors' | 'tutor-editor' | 'registration-editor' | 'keynotes' | 'keynote-editor' | 'references' | 'reference-editor' | 'invoices' | 'invoice-editor'>(() => {
    const saved = typeof window !== 'undefined' ? window.localStorage.getItem('admin_active_tab') : null;
    if (saved) return saved as any;
    return 'courses';
  });
  const [selectedItemId, setSelectedItemId] = useState<string>(() => {
    const saved = typeof window !== 'undefined' ? window.localStorage.getItem('admin_selected_id') : null;
    return saved || '';
  });
  const [partnerRefreshTrigger, setPartnerRefreshTrigger] = useState(0);
  const [tutorRefreshTrigger, setTutorRefreshTrigger] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuthenticatedUser();
  }, [navigate]);

  // Persist active tab and selection so reloads restore the current view
  useEffect(() => {
    try {
      window.localStorage.setItem('admin_active_tab', activeTab);
      window.localStorage.setItem('admin_selected_id', selectedItemId || '');
    } catch {}
  }, [activeTab, selectedItemId]);

  const checkAuthenticatedUser = async () => {
    try {
      const user = await getCurrentUser();
      
      // Create admin user object from Cognito user
      const adminUser: AdminUser = {
        userId: user.userId,
        username: user.username,
        name: user.signInDetails?.loginId || user.username,
        loginTime: new Date().toISOString()
      };
      
      setAdminUser(adminUser);
      
      // Track admin login
      trackAdminAction('Admin Login', `User: ${user.username}`);
    } catch (error) {
      console.error('User not authenticated:', error);
      // Localhost dev bypass (optional)
      if ((window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') &&
          process.env.REACT_APP_DEV_ADMIN_BYPASS === 'true') {
        try {
          const bypass = window.localStorage.getItem('dev_admin_bypass');
          if (bypass === '1') {
            const adminUser: AdminUser = {
              userId: 'dev-local-user',
              username: 'wilhelm@mintkitchen.de',
              name: 'Dev Admin (Local)',
              loginTime: new Date().toISOString()
            };
            setAdminUser(adminUser);
            return;
          }
        } catch {}
      }
      navigate('/admin/login');
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/admin/login');
    } catch (error) {
      console.error('Error signing out:', error);
      navigate('/admin/login');
    }
  };

  // Navigation helpers for edit functionality
  const handleEditCourse = (courseId: string) => {
    setSelectedItemId(courseId);
    setActiveTab('editor');
  };

  const handleEditPartner = (partnerId: string) => {
    setSelectedItemId(partnerId);
    setActiveTab('partner-editor');
  };

  const handleEditTutor = (tutorId: string) => {
    setSelectedItemId(tutorId);
    setActiveTab('tutor-editor');
  };

  const handleEditRegistration = (registrationId: string) => {
    setSelectedItemId(registrationId);
    setActiveTab('registration-editor');
  };

  const handleEditKeynote = (keynoteId: string) => {
    setSelectedItemId(keynoteId);
    setActiveTab('keynote-editor');
  };

  const handleEditReference = (referenceId: string) => {
    setSelectedItemId(referenceId);
    setActiveTab('reference-editor');
  };

  const handleEditInvoice = (groupKey: string) => {
    console.log('🎯 AdminDashboard: handleEditInvoice called with groupKey:', groupKey);
    setSelectedItemId(groupKey);
    // Force update localStorage immediately
    try {
      window.localStorage.setItem('admin_selected_id', groupKey);
    } catch {}
    setActiveTab('invoice-editor');
    console.log('✅ AdminDashboard: Set selectedItemId to:', groupKey, 'and switched to invoice-editor');
  };



  const renderContent = () => {
    switch (activeTab) {
      case 'courses':
        return <AdminCoursesOverview onEditCourse={handleEditCourse} />;
      case 'editor':
        return <AdminCourseEditor selectedCourseId={selectedItemId} onBack={() => setActiveTab('courses')} />;
      case 'registrations':
        return <AdminRegistrations onEditRegistration={handleEditRegistration} />;
      case 'contacts':
        return <AdminContacts />;
      case 'partners':
        return <AdminPartners onEditPartner={handleEditPartner} refreshTrigger={partnerRefreshTrigger} />;
      case 'partner-editor':
        return <AdminPartnerEditor 
          selectedPartnerId={selectedItemId} 
          onBack={() => {
            setActiveTab('partners');
            setPartnerRefreshTrigger(prev => prev + 1); // Trigger refresh
          }} 
        />;
      case 'tutors':
        return <AdminTutors onEditTutor={handleEditTutor} refreshTrigger={tutorRefreshTrigger} />;
      case 'tutor-editor':
        return <AdminTutorEditor 
          selectedTutorId={selectedItemId} 
          onBack={() => {
            setActiveTab('tutors');
            setTutorRefreshTrigger(prev => prev + 1); // Trigger refresh
          }} 
        />;
      case 'registration-editor':
        return <AdminRegistrationEditor selectedRegistrationId={selectedItemId} onBack={() => setActiveTab('registrations')} />;
      case 'keynotes':
        return <AdminKeynotesOverview onEditKeynote={handleEditKeynote} />;
      case 'keynote-editor':
        return <AdminKeynoteEditor selectedKeynoteId={selectedItemId} onBack={() => setActiveTab('keynotes')} />;
      case 'references':
        return <AdminReferences onEditReference={handleEditReference} />;
      case 'reference-editor':
        return <AdminReferenceEditor selectedReferenceId={selectedItemId} onBack={() => setActiveTab('references')} />;
      case 'invoices':
        return <AdminInvoices onEditInvoice={handleEditInvoice} />;
      case 'invoice-editor':
        console.log('🔄 AdminDashboard: Rendering AdminInvoiceEditor with selectedItemId:', selectedItemId);
        return <AdminInvoiceEditor selectedGroupKey={selectedItemId} onBack={() => setActiveTab('invoices')} />;
      default:
        return <AdminCoursesOverview onEditCourse={handleEditCourse} />;
    }
  };

  if (!adminUser) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Sidebar Navigation */}
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <div className="admin-logo-section">
            <a href="/" title="Go to Home">
              <img 
                src="/assets/images/common/mintKitchenLogo.svg" 
                alt="MintKitchen" 
                className="admin-logo"
              />
            </a>
            <h2>Admin Dashboard</h2>
          </div>
          <div className="admin-user-info">
            <span className="admin-name">{adminUser.name}</span>
            <span className="admin-role">Administrator</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          {/* Courses Row */}
          <div className="nav-row">
            <button
              className={`nav-item main-nav ${activeTab === 'courses' ? 'active' : ''}`}
              onClick={() => setActiveTab('courses')}
            >
              <span className="nav-label" style={{ fontWeight: 'bold', color: '#008F99', fontSize: '16px', textTransform: 'uppercase' }}>COURSES</span>
            </button>
            <button
              className={`nav-item editor-nav-item ${activeTab === 'editor' ? 'active' : ''}`}
              onClick={() => {
                setSelectedItemId('');
                setActiveTab('editor');
              }}
              title="Course Editor"
            >
              <span className="edit-icon">✎</span>
            </button>
          </div>
          
          {/* Partners Row */}
          <div className="nav-row">
            <button
              className={`nav-item main-nav ${activeTab === 'partners' ? 'active' : ''}`}
              onClick={() => setActiveTab('partners')}
            >
              <span className="nav-label" style={{ fontWeight: 'bold', color: '#008F99', fontSize: '16px', textTransform: 'uppercase' }}>PARTNERS</span>
            </button>
            <button
              className={`nav-item editor-nav-item ${activeTab === 'partner-editor' ? 'active' : ''}`}
              onClick={() => {
                setSelectedItemId('');
                setActiveTab('partner-editor');
              }}
              title="Partner Editor"
            >
              <span className="edit-icon">✎</span>
            </button>
          </div>
          
          {/* Tutors Row */}
          <div className="nav-row">
            <button
              className={`nav-item main-nav ${activeTab === 'tutors' ? 'active' : ''}`}
              onClick={() => setActiveTab('tutors')}
            >
              <span className="nav-label" style={{ fontWeight: 'bold', color: '#008F99', fontSize: '16px', textTransform: 'uppercase' }}>TUTORS</span>
            </button>
            <button
              className={`nav-item editor-nav-item ${activeTab === 'tutor-editor' ? 'active' : ''}`}
              onClick={() => {
                setSelectedItemId('');
                setActiveTab('tutor-editor');
              }}
              title="Tutor Editor"
            >
              <span className="edit-icon">✎</span>
            </button>
          </div>
          
          {/* Participants Row */}
          <div className="nav-row">
            <button
              className={`nav-item main-nav ${activeTab === 'registrations' ? 'active' : ''}`}
              onClick={() => setActiveTab('registrations')}
            >
              <span className="nav-label" style={{ fontWeight: 'bold', color: '#008F99', fontSize: '16px', textTransform: 'uppercase' }}>PARTICIPANTS</span>
            </button>
            <button
              className={`nav-item editor-nav-item ${activeTab === 'registration-editor' ? 'active' : ''}`}
              onClick={() => {
                setSelectedItemId('');
                setActiveTab('registration-editor');
              }}
              title="Registration Editor"
            >
              <span className="edit-icon">✎</span>
            </button>
          </div>
          
          {/* Invoices Row */}
          <div className="nav-row">
            <button
              className={`nav-item main-nav ${activeTab === 'invoices' ? 'active' : ''}`}
              onClick={() => setActiveTab('invoices')}
            >
              <span className="nav-label" style={{ fontWeight: 'bold', color: '#008F99', fontSize: '16px', textTransform: 'uppercase' }}>INVOICES</span>
            </button>
            <button
              className={`nav-item editor-nav-item ${activeTab === 'invoice-editor' ? 'active' : ''}`}
              onClick={() => setActiveTab('invoice-editor')}
              title="Invoice Editor"
            >
              <span className="edit-icon">✎</span>
            </button>
          </div>
          
          {/* Keynotes Row */}
          <div className="nav-row">
            <button
              className={`nav-item main-nav ${activeTab === 'keynotes' ? 'active' : ''}`}
              onClick={() => setActiveTab('keynotes')}
            >
              <span className="nav-label" style={{ fontWeight: 'bold', color: '#008F99', fontSize: '16px', textTransform: 'uppercase' }}>KEYNOTES</span>
            </button>
            <button
              className={`nav-item editor-nav-item ${activeTab === 'keynote-editor' ? 'active' : ''}`}
              onClick={() => {
                setSelectedItemId('');
                setActiveTab('keynote-editor');
              }}
              title="Keynote Editor"
            >
              <span className="edit-icon">✎</span>
            </button>
          </div>

          {/* References Row */}
          <div className="nav-row">
            <button
              className={`nav-item main-nav ${activeTab === 'references' ? 'active' : ''}`}
              onClick={() => setActiveTab('references')}
            >
              <span className="nav-label" style={{ fontWeight: 'bold', color: '#008F99', fontSize: '16px', textTransform: 'uppercase' }}>REFERENCES</span>
            </button>
            <button
              className={`nav-item editor-nav-item ${activeTab === 'reference-editor' ? 'active' : ''}`}
              onClick={() => {
                setSelectedItemId('');
                setActiveTab('reference-editor');
              }}
              title="Reference Editor"
            >
              <span className="edit-icon">✎</span>
            </button>
          </div>

          {/* Contacts Row (no editor) */}
          <div className="nav-row">
            <button
              className={`nav-item main-nav ${activeTab === 'contacts' ? 'active' : ''}`}
              onClick={() => setActiveTab('contacts')}
            >
              <span className="nav-label" style={{ fontWeight: 'bold', color: '#008F99', fontSize: '16px', textTransform: 'uppercase' }}>CONTACTS</span>
            </button>
            <div className="nav-spacer"></div>
          </div>
        </nav>

        <div className="sidebar-footer">
          <button 
            className="nav-item public-btn" 
            onClick={() => navigate('/')}
            title="Return to public website"
          >
            <span className="nav-label">Back to Public</span>
          </button>
          <button 
            className="nav-item logout-btn" 
            onClick={handleLogout}
            title="Sign out and return to login"
          >
            <span className="nav-label">Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="admin-main-content">
        <div className="content-header">
          <h1>{activeTab === 'courses' ? 'Courses' : 
               activeTab === 'editor' ? 'Course Editor' : 
               activeTab === 'registrations' ? 'Participants' :
               activeTab === 'contacts' ? 'Contact Submissions' :
               activeTab === 'partners' ? 'Partners' :
               activeTab === 'partner-editor' ? 'Partner Editor' :
               activeTab === 'tutors' ? 'Tutors Management' :
               activeTab === 'tutor-editor' ? 'Tutor Editor' :
               activeTab === 'registration-editor' ? 'Registration Editor' :
               activeTab === 'keynotes' ? 'Keynotes' :
               activeTab === 'keynote-editor' ? 'Keynote Editor' :
               activeTab === 'references' ? 'References' :
               activeTab === 'reference-editor' ? 'Reference Editor' :
               activeTab === 'invoices' ? 'Invoices' :
               activeTab === 'invoice-editor' ? 'Invoice Editor' :
               'Dashboard'}</h1>
          {/* Item selectors for editor and registrations tabs */}
          {(activeTab === 'editor' || activeTab === 'partner-editor' || activeTab === 'tutor-editor' || activeTab === 'registration-editor' || activeTab === 'registrations' || activeTab === 'contacts' || activeTab === 'partners' || activeTab === 'keynotes' || activeTab === 'keynote-editor' || activeTab === 'references' || activeTab === 'reference-editor' || activeTab === 'invoices' || activeTab === 'invoice-editor') && (
            <div id="header-selector-container" style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '16px' 
            }}>
              {/* Selector will be injected here by the components */}
            </div>
          )}
        </div>
        
        <div className="content-body">
          {renderContent()}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;